import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import type { AdminRole } from '../lib/cognito';

/**
 * Guards a route: requires an active session; optionally enforces a minimum role.
 * Redirects signed-out users to /login (preserving the attempted path for post-login redirect).
 */
export function ProtectedRoute({
  children,
  requireRole,
}: {
  children: ReactNode;
  requireRole?: AdminRole;
}) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.kind === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen text-muted text-sm">
        Loading session…
      </div>
    );
  }

  if (auth.kind === 'signed-out') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requireRole && !auth.hasRole(requireRole)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-2">
        <div className="text-lg font-semibold">Insufficient permissions</div>
        <div className="text-sm text-muted">
          This page requires <code>{requireRole}</code> role or higher.
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
